import React, {
  FC,
  ReactNode,
  useImperativeHandle,
  forwardRef,
  useState,
  useEffect,
} from "react";
import "./StepperContent.scss";

interface Step {
  label: string;
  content: ReactNode;
  onValidate?: () => Promise<boolean>;
}

interface StepperContentProps {
  steps: Step[];
  currentStep?: number;
  onStepChange?: (step: number) => void;
  showNavigation?: boolean;
  showNextButton?: boolean | number[];
  showPreviousButton?: boolean | number[];
  nextButtonLabel?: string;
  previousButtonLabel?: string;
}

export interface StepperContentRef {
  next: () => Promise<void>;
  previous: () => void;
  goToStep: (step: number) => void;
  getCurrentStep: () => number;
}

const StepperContent = forwardRef<StepperContentRef, StepperContentProps>(
  (
    {
      steps,
      currentStep = 0,
      onStepChange,
      showNavigation = true,
      showNextButton = true,
      showPreviousButton = true,
      nextButtonLabel = "Próximo",
      previousButtonLabel = "Voltar",
    },
    ref,
  ) => {
    const [internalStep, setInternalStep] = useState(currentStep);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
      setInternalStep(currentStep);
    }, [currentStep]);

    const handleNext = async () => {
      if (internalStep >= steps.length - 1) return;

      const currentStepConfig = steps[internalStep];
      if (currentStepConfig.onValidate) {
        setIsLoading(true);
        try {
          const canProceed = await currentStepConfig.onValidate();
          setIsLoading(false);
          if (!canProceed) return;
        } catch (error) {
          setIsLoading(false);
          console.error("Erro ao validar passo:", error);
          return;
        }
      }

      const newStep = internalStep + 1;
      setInternalStep(newStep);
      onStepChange?.(newStep);
    };

    const handlePrevious = () => {
      if (internalStep <= 0) return;

      const newStep = internalStep - 1;
      setInternalStep(newStep);
      onStepChange?.(newStep);
    };

    const goToStep = (step: number) => {
      if (step < 0 || step >= steps.length) return;
      setInternalStep(step);
      onStepChange?.(step);
    };

    useImperativeHandle(ref, () => ({
      next: handleNext,
      previous: handlePrevious,
      goToStep,
      getCurrentStep: () => internalStep,
    }));

    const shouldShowPrevious = () => {
      if (!showPreviousButton) return false;
      if (internalStep === 0) return false;
      if (showPreviousButton === true) return true;
      if (Array.isArray(showPreviousButton))
        return showPreviousButton.includes(internalStep);
      return false;
    };

    const shouldShowNext = () => {
      if (!showNextButton) return false;
      if (internalStep >= steps.length - 1) return false;
      if (showNextButton === true) return true;
      if (Array.isArray(showNextButton))
        return showNextButton.includes(internalStep);
      return false;
    };

    return (
      <section className="stepper-content-section">
        <div className="stepper-content-wrapper">
          {steps.map((step, index) => (
            <div
              key={index}
              className="stepper-content-item"
              style={{ display: index === internalStep ? "block" : "none" }}
            >
              {step.content}
            </div>
          ))}
        </div>

        {showNavigation && (
          <div className="stepper-navigation">
            {shouldShowPrevious() && (
              <button
                className="btn-stepper btn-stepper-previous"
                onClick={handlePrevious}
                disabled={isLoading}
              >
                <span className="btn-arrow">←</span>
                <span>{previousButtonLabel}</span>
              </button>
            )}

            {shouldShowNext() && (
              <button
                className="btn-stepper btn-stepper-next"
                onClick={handleNext}
                disabled={isLoading}
              >
                <span>{nextButtonLabel}</span>
                <span className="btn-arrow">{isLoading ? "⟳" : "→"}</span>
              </button>
            )}
          </div>
        )}
      </section>
    );
  },
);

StepperContent.displayName = "StepperContent";

export default StepperContent;
